"use client"
import { useState, useEffect, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBox, faFilter, faTimes, faFileExcel, faInr } from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"
import { baseUrl } from "../../config/baseUrl"
import moment from "moment/moment"
import PermissionGuard from "../PermissionGuard/PermissionGuard"
import { PERMISSIONS } from "../../utils/permissions"

export default function PackageReport() {
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [reportSummary, setReportSummary] = useState({
    totalSales: 0,
    totalCouponsUsed: 0,
    totalAmountGivenToLibraries: 0,
    totalPurchases: 0,
  })

  useEffect(() => {
    const today = moment().format("YYYY-MM-DD")
    setStartDate(today)
    setEndDate(today)
  }, [])

  useEffect(() => {
    if (startDate && endDate) {
      fetchPackageReport()
    }
  }, [startDate, endDate])

  const fetchPackageReport = async () => {
    try {
      setLoading(true)
      const url = `${baseUrl}/admin/dashboard/mock-test?startDate=${startDate}&endDate=${endDate}`

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      })
      const data = await response.json()

      if (data.status) {
        const packageData = data.data || []
        setPackages(packageData)

        const summary = packageData.reduce(
          (acc, pkg) => {
            return {
              totalSales: acc.totalSales + (pkg.amount || 0),
              totalCouponsUsed: acc.totalCouponsUsed + (pkg.couponCode ? 1 : 0),
              totalAmountGivenToLibraries: acc.totalAmountGivenToLibraries + (pkg.commissionAmount || 0),
              totalPurchases: acc.totalPurchases + 1,
            }
          },
          { totalSales: 0, totalCouponsUsed: 0, totalAmountGivenToLibraries: 0, totalPurchases: 0 },
        )

        setReportSummary(summary)
      } else {
        toast.error(data.message || "Failed to fetch package report")
        setPackages([])
      }
    } catch (error) {
      console.error("Error fetching package report:", error)
      toast.error("Failed to fetch package report")
      setPackages([])
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = () => {
    const today = moment().format("YYYY-MM-DD")
    setStartDate(today)
    setEndDate(today)
  }

  const handleExportExcel = async () => {
    try {
      const url = `${baseUrl}/admin/dashboard/mock-test-excel?startDate=${startDate}&endDate=${endDate}`

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      })

      if (response.ok) {
        const blob = await response.blob()
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = downloadUrl
        link.download = `mock-test-report-${moment().format("YYYY-MM-DD")}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(downloadUrl)
        toast.success("Package report exported successfully!")
      } else {
        toast.error("Failed to export package report")
      }
    } catch (error) {
      console.error("Error exporting package report:", error)
      toast.error("Failed to export package report")
    }
  }

  const getActiveFiltersCount = () => {
    let count = 0
    if (startDate) count++
    if (endDate) count++
    return count
  }

  const getDateRangeDisplay = () => {
    if (!startDate || !endDate) return "N/A"

    if (startDate === endDate) {
      return moment(startDate).format("D MMM YYYY")
    } else {
      return `${moment(startDate).format("D MMM YYYY")} - ${moment(endDate).format("D MMM YYYY")}`
    }
  }

  const scrollContainerRef = useRef(null)

  useEffect(() => {
    const slider = scrollContainerRef.current
    if (!slider) return

    let isDown = false
    let startX
    let scrollLeft

    const mouseDownHandler = (e) => {
      isDown = true
      startX = e.pageX - slider.offsetLeft
      scrollLeft = slider.scrollLeft
      slider.classList.add("dragging")
    }

    const mouseLeaveHandler = () => {
      isDown = false
      slider.classList.remove("dragging")
    }

    const mouseUpHandler = () => {
      isDown = false
      slider.classList.remove("dragging")
    }

    const mouseMoveHandler = (e) => {
      if (!isDown) return
      e.preventDefault()
      const x = e.pageX - slider.offsetLeft
      const walk = (x - startX) * 1.5
      slider.scrollLeft = scrollLeft - walk
    }

    slider.addEventListener("mousedown", mouseDownHandler)
    slider.addEventListener("mouseleave", mouseLeaveHandler)
    slider.addEventListener("mouseup", mouseUpHandler)
    slider.addEventListener("mousemove", mouseMoveHandler)

    return () => {
      slider.removeEventListener("mousedown", mouseDownHandler)
      slider.removeEventListener("mouseleave", mouseLeaveHandler)
      slider.removeEventListener("mouseup", mouseUpHandler)
      slider.removeEventListener("mousemove", mouseMoveHandler)
    }
  }, [])

  return (
    <>
      <section className="main-sec">
        <div className="container-fluid">
          {/* Header */}
          <div className="row mb-4">
            <div className="col-lg-8">
              <div className="dashboard-title">
                <h4 className="dash-head mb-2">
                  <FontAwesomeIcon icon={faBox} className="me-2" />
                  Package Report (Mock Test Purchases)
                </h4>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="export-buttons">
                <PermissionGuard permission={PERMISSIONS.PACKAGE_REPORT_EXPORT}>
                  <button className="btn btn-success btn-sm me-2" onClick={handleExportExcel}>
                    <FontAwesomeIcon icon={faFileExcel} className="me-1" />
                    Export Excel
                  </button>
                </PermissionGuard>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          {/* <div className="row mb-4">
                        <div className="col-lg-12">
                            <div className="summary-cards-grid">
                                <div className="summary-card total-sales">
                                    <div className="summary-icon">
                                        <FontAwesomeIcon icon={faInr} />
                                    </div>
                                    <div className="summary-content">
                                        <h6 className="text-white">Total Sales</h6>
                                        <h4 className="text-white">₹{reportSummary.totalSales.toLocaleString()}</h4>
                                    </div>
                                </div>
                                <div className="summary-card total-coupons">
                                    <div className="summary-icon">
                                        <FontAwesomeIcon icon={faBox} />
                                    </div>
                                    <div className="summary-content">
                                        <h6 className="text-white">Coupons Used</h6>
                                        <h4 className="text-white">{reportSummary.totalCouponsUsed}</h4>
                                    </div>
                                </div>
                                <div className="summary-card amount-to-libraries">
                                    <div className="summary-icon">
                                        <FontAwesomeIcon icon={faInr} />
                                    </div>
                                    <div className="summary-content">
                                        <h6 className="text-white">Amount Given to Libraries</h6>
                                        <h4 className="text-white">₹{reportSummary.totalAmountGivenToLibraries.toLocaleString()}</h4>
                                    </div>
                                </div>
                                <div className="summary-card total-purchases">
                                    <div className="summary-icon">
                                        <FontAwesomeIcon icon={faBox} />
                                    </div>
                                    <div className="summary-content">
                                        <h6 className="text-white">Total Purchases</h6>
                                        <h4 className="text-white">{reportSummary.totalPurchases}</h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> */}

          {/* Filters Card */}
          <div className="card filter-card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <FontAwesomeIcon icon={faFilter} className="me-2" />
                Filters{" "}
                {getActiveFiltersCount() > 0 && (
                  <span className="badge bg-primary ms-2">{getActiveFiltersCount()}</span>
                )}
              </h6>
              {getActiveFiltersCount() > 0 && (
                <button className="btn btn-sm btn-outline-secondary" onClick={handleClearFilters}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Reset to Today
                </button>
              )}
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3 col-lg-2">
                  <label className="form-label small fw-medium">Start Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3 col-lg-2">
                  <label className="form-label small fw-medium">End Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {getActiveFiltersCount() > 0 && (
            <div className="active-filters-display mb-3">
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <small className="text-muted fw-medium">Active Filters:</small>
                {startDate && <span className="filter-tag">From: {moment(startDate).format("MMM DD, YYYY")}</span>}
                {endDate && <span className="filter-tag">To: {moment(endDate).format("MMM DD, YYYY")}</span>}
              </div>
            </div>
          )}

          {/* Results Summary */}
          <div className="results-summary mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                Package Records
                <span className="text-muted ms-2">({packages.length} results)</span>
              </h6>
            </div>
          </div>

          {/* Table Card */}
          <div className="card table-card">
            <div className="table-responsive" ref={scrollContainerRef}>
              <table className="table table-hover align-middle mb-0">
                <thead className="table-header">
                  <tr>
                    <th className="sticky-col sticky-col-1 text-black">Code</th>
                    <th>Price</th>
                    <th>Coupon</th>
                    <th>Date</th>
                    <th>Final Price</th>
                    <th>Billing Number</th>
                    <th>Student Code</th>
                    <th>Student Name</th>
                    <th>Refral Code</th>
                    <th>Name Of Library / Category</th>
                    <th>Amount (given to lib)</th>
                    <th>Author</th>
                    <th>Author Name</th>
                    <th>Amount (given to auth)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="14" className="text-center py-5">
                        <div className="loading-container">
                          <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mb-0 text-muted">Loading package report...</p>
                        </div>
                      </td>
                    </tr>
                  ) : packages.length === 0 ? (
                    <tr>
                      <td colSpan="14" className="text-center py-5">
                        <div className="empty-state">
                          <FontAwesomeIcon icon={faBox} size="3x" className="text-muted mb-3" />
                          <h5 className="text-muted">No Package Records Found</h5>
                          <p className="text-muted mb-0">
                            {getActiveFiltersCount() > 0
                              ? "Try adjusting your filter criteria"
                              : "No package records available at the moment"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    packages.map((pkg, index) => {
                      // Generate unique Billing Number for package
                      const billingNumber = `${pkg.packageId?.packageCode || "N/A"}_${pkg._id?.slice(-5) || "00000"}`;

                      return (
                        <tr key={index}>
                          <td className="sticky-col sticky-col-1">
                            <span className="code-badge">{pkg.packageId?.packageCode || "N/A"}</span>
                          </td>
                          <td>
                            <span className="price-badge">₹{pkg.packageId?.price || 0}</span>
                          </td>
                          <td>
                            {pkg.couponCode ? (
                              <span className="coupon-badge">{pkg.couponCode}</span>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </td>
                          <td>
                            <span className="date-text">{getDateRangeDisplay()}</span>
                          </td>
                          <td>
                            <span className="final-price">₹{pkg.amount || 0}</span>
                          </td>
                          <td>
                            <span className="billing-number">{billingNumber}</span>
                          </td>
                          <td>
                            <span className="student-code">{pkg.userId?.userCode || "N/A"}</span>
                          </td>
                          <td>
                            <strong>{pkg.userId?.name || "N/A"}</strong>
                          </td>
                          <td>
                            {pkg.refCode ? (
                              <span className="refral-badge">{pkg.refCode}</span>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </td>
                          <td>
                            <span className="library-category">
                              {pkg.libraryName || "N/A"} / {pkg.libraryCategory || "N/A"}
                            </span>
                          </td>
                          <td>
                            <span className="amount-given">₹{pkg.commissionAmount || 0}</span>
                          </td>
                          <td>
                            <span className="author-text">{pkg.author || "N/A"}</span>
                          </td>
                          <td>
                            <span className="author-name">{pkg.authorName || "N/A"}</span>
                          </td>
                          <td>
                            <span className="amount-given">₹{pkg.authorCommissionAmount || 0}</span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>

              </table>
            </div>
          </div>
        </div>
      </section>

      <style>
        {`
        .table-responsive {
          cursor: grab;
        }

        .table-responsive.dragging {
          cursor: grabbing;
          user-select: none;
        }

        .main-sec {
          background-color: #f8f9fa;
          min-height: 100vh;
          padding: 2rem 0;
        }

        .dashboard-title .dash-head {
          color: #2c3e50;
          font-weight: 600;
        }

        .export-buttons {
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
        }

        .summary-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .summary-card {
          background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
          color: white;
          padding: 1.25rem;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          transition: transform 0.2s ease;
        }

        .summary-card:hover {
          transform: translateY(-2px);
        }

        .summary-card.total-sales {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        .summary-card.total-coupons {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }

        .summary-card.amount-to-libraries {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        }

        .summary-card.total-purchases {
          background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
        }

        .summary-icon {
          background: rgba(255,255,255,0.15);
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
        }

        .summary-content h6 {
          margin: 0;
          font-size: 0.875rem;
          opacity: 0.95;
          font-weight: 500;
        }

        .summary-content h4 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 700;
        }

        .filter-card {
          border: none;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }

        .filter-card .card-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #e5e7eb;
        }

        .active-filters-display {
          background-color: #eff6ff;
          border: 1px solid #dbeafe;
          border-radius: 0.375rem;
          padding: 0.75rem;
        }

        .filter-tag {
          background-color: #60a5fa;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .results-summary {
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.375rem;
          padding: 0.75rem 1rem;
        }

        .table-card {
          border: none;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }

        .table-header th {
          background-color: #f9fafb;
          border-bottom: 2px solid #e5e7eb;
          font-weight: 600;
          font-size: 0.8rem;
          padding: 0.75rem 0.5rem;
          white-space: nowrap;
          text-align: center;
          color: #374151;
        }

        .table td {
          padding: 0.75rem 0.5rem;
          vertical-align: middle;
          border-bottom: 1px solid #f3f4f6;
          font-size: 0.875rem;
          text-align: center;
        }

        .table tbody tr:hover {
          background-color: #f9fafb;
        }

        .sticky-col {
          position: sticky;
          background: white;
          z-index: 10;
          box-shadow: 2px 0 5px rgba(0, 0, 0, 0.04);
        }

        .sticky-col-1 {
          left: 0;
          z-index: 12;
        }

        .sticky-col-2 {
          left: 100px;
          z-index: 11;
        }

        .table-header .sticky-col {
          background: #f9fafb;
        }

        .table-header .sticky-col-1 {
          z-index: 13;
        }

        .table-header .sticky-col-2 {
          z-index: 12;
        }

        .table tbody tr:hover .sticky-col {
          background: #f9fafb;
        }

        .code-badge {
          background-color: #8b5cf6;
          color: white;
          padding: 5px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
        }

        .price-badge {
          background-color: #6366f1;
          color: white;
          padding: 5px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
        }

        .coupon-badge {
          background-color: #f59e0b;
          color: white;
          padding: 5px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
        }

        .final-price {
          color: #059669;
          font-weight: 700;
          font-size: 15px;
        }

        .billing-number {
          color: #6b7280;
          font-weight: 500;
          font-size: 13px;
        }

        .student-code {
          color: #6b7280;
          font-weight: 500;
          font-size: 13px;
        }

        .refral-badge {
          background-color: #10b981;
          color: white;
          padding: 5px 10px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
        }

        .library-category {
          color: #4b5563;
          font-weight: 500;
          font-size: 13px;
        }

        .amount-given {
          color: #4f46e5;
          font-weight: 700;
          font-size: 15px;
        }

        .author-text {
          color: #6b7280;
          font-weight: 500;
          font-size: 13px;
        }

        .author-name {
          color: #374151;
          font-weight: 600;
          font-size: 13px;
        }

        .date-text {
          color: #6b7280;
          font-weight: 500;
          font-size: 13px;
        }

        .loading-container,
        .empty-state {
          padding: 2rem;
        }

        @media (max-width: 1200px) {
          .table-header th,
          .table td {
            padding: 0.5rem 0.25rem;
            font-size: 0.8rem;
          }

          .sticky-col-2 {
            left: 80px;
          }
        }

        @media (max-width: 768px) {
          .summary-cards-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .export-buttons {
            justify-content: center;
            margin-top: 1rem;
          }

          .table-responsive {
            font-size: 0.75rem;
          }

          .table-header th,
          .table td {
            padding: 0.4rem 0.2rem;
          }

          .sticky-col-2 {
            left: 70px;
          }
        }

        @media (max-width: 576px) {
          .main-sec {
            padding: 1rem 0;
          }

          .summary-cards-grid {
            grid-template-columns: 1fr;
          }

          .summary-card {
            padding: 1rem;
          }

          .summary-content h4 {
            font-size: 1.25rem;
          }

          .filter-card .card-body .row {
            gap: 0.5rem;
          }

          .sticky-col-2 {
            left: 60px;
          }
        }
        `}
      </style>
    </>
  )
}
